import { type FC } from 'react';

import { _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';

import type { SuccessfulOverviewSection } from '../../OverviewAccordion.model';
import { canEditOverviewTreatment } from '../../overviewSectionAccess';
import EditableOverviewTreatmentsTable from '../OverviewTreatmentsTable/EditableOverviewTreatmentsTable';
import OverviewTreatmentsTable from '../OverviewTreatmentsTable/OverviewTreatmentsTable';

interface TreatmentsSectionContentProps {
  section: SuccessfulOverviewSection<'treatments' | 'diseases'>;
  study?: Study;
  diseaseInduction?: boolean;
  onTreatmentSaved?: () => void;
}

const SECTION_CLASSIFICATIONS = {
  treatments: 'treatment',
  diseases: 'disease_induction',
} as const;

/**
 * Renders Overview treatments or diseases with the same read columns as the matching
 * study-groups tab, and the same edit modal behind the label when the caller can edit them.
 */
const TreatmentsSectionContent: FC<TreatmentsSectionContentProps> = ({
  section,
  study,
  diseaseInduction = false,
  onTreatmentSaved,
}) => {
  const classification = SECTION_CLASSIFICATIONS[section.type];
  const showClassification = section.type === 'treatments';

  return (
    <div data-test-component="TreatmentsSectionContent" data-test-element="container">
      {canEditOverviewTreatment(study, classification, diseaseInduction) && _notNil(study) ? (
        <EditableOverviewTreatmentsTable
          rows={section.rows}
          study={study}
          type={classification}
          showClassification={showClassification}
          onTreatmentSaved={onTreatmentSaved}
        />
      ) : (
        <OverviewTreatmentsTable rows={section.rows} showClassification={showClassification} />
      )}
    </div>
  );
};

export default TreatmentsSectionContent;
